// InspectionCards — the cards view of the inspections list (the toolbar's
// cards/table switch picks between this and the DataTable branch). Render-only:
// the page owns the query, the load-more accumulation and the gating, and both
// views share the same listing API. Cards are not clickable — the footer Edit
// button (canEdit-gated via the onEdit prop) is the only edit entry point, and
// the lifecycle kebab verbs (Duplicate/Suspend/Archive/Delete) stay table-only.

import { useTranslation } from 'react-i18next';
import { Avatar, Button, CardsLoadMore, Chip, DataCards, Icon } from '@/components/common';
import type { InspectionListItem } from '@/api/endpoints/inspectionApi';
import InspectionAssigneesCell from './InspectionAssigneesCell';
import { PRIORITY_META, STATE_META, structureLines } from './inspectionMeta';
import toneOf from '@/utils/avatarTone';
import initialsOf from '@/utils/initials';
import { fmtDate } from '@/utils/formatDate';

interface InspectionCardsProps {
  items: InspectionListItem[];
  /** First fetch of an empty grid — items hidden, status shown. */
  loading: boolean;
  error: boolean;
  hasMore: boolean;
  /** Next-page fetch in flight — the Load-more button spins. */
  loadingMore: boolean;
  onLoadMore: () => void;
  /** Archived tab — only the empty-state icon differs (table parity). */
  archivedTab: boolean;
  /** Footer Edit button; omit when the user lacks the edit verb. */
  onEdit?: (inspection: InspectionListItem) => void;
}

const InspectionCards = ({
  items,
  loading,
  error,
  hasMore,
  loadingMore,
  onLoadMore,
  archivedTab,
  onEdit,
}: InspectionCardsProps) => {
  const { t } = useTranslation();

  const renderCard = (i: InspectionListItem) => {
    const state = STATE_META[i.state];
    const priority = i.priority ? PRIORITY_META[i.priority] : null;
    const structure = structureLines(i, t);
    const shownTags = i.tags.slice(0, 2);
    const extraTags = i.tags.length - shownTags.length;

    return (
      <div className='inspection-card'>
        <span className='inspection-card__head'>
          <span className='inspection-card__tile'>
            <Icon name='clipboard-list' size={18} />
          </span>
          <span className='inspection-card__title'>{i.title || '—'}</span>
        </span>

        <span className='inspection-card__meta'>
          <Chip variant={state.chip} iconStart={state.icon}>
            {t(`inspection_state_${i.state}`)}
          </Chip>
          <span className='inspection-card__code'>
            INSP-{String(i.referenceNumber).padStart(2, '0')}
          </span>
        </span>

        {i.description && <span className='inspection-card__desc'>{i.description}</span>}

        {i.form?.title && (
          <span className='inspection-card__row'>
            <Icon name={i.form.icon} size={15} />
            {i.form.title}
          </span>
        )}
        {i.project?.title && (
          <span className='inspection-card__row'>
            <Icon name={i.project.icon} size={15} />
            {i.project.title}
          </span>
        )}

        {structure && (
          <span className='inspection-card__structure'>
            <Icon name={structure.icon} size={16} className='inspection-card__structure-icon' />
            <span className='inspection-card__structure-label'>
              <span className='inspection-card__structure-name'>{structure.name}</span>
              <span className='inspection-card__structure-sub'>{structure.sub}</span>
            </span>
          </span>
        )}

        {(priority || i.tags.length > 0 || i.startDate || i.endDate) && (
          <span className='inspection-card__chips'>
            {priority && (
              <Chip variant={priority.chip} iconStart={priority.icon}>
                {t(`inspection_priority_${i.priority}`)}
              </Chip>
            )}
            {shownTags.map((tag) => (
              <Chip key={tag.id} variant='neutral' iconStart='tag'>
                {tag.title}
              </Chip>
            ))}
            {extraTags > 0 && <span className='inspection-card__more'>+{extraTags}</span>}
            {(i.startDate || i.endDate) && (
              <span className='inspection-card__dates'>
                {fmtDate(i.startDate)}
                <Icon name='arrow-right' size={14} className='inspection-card__dates-arrow' />
                {fmtDate(i.endDate)}
              </span>
            )}
          </span>
        )}

        <InspectionAssigneesCell inspection={i} />
      </div>
    );
  };

  const renderFooter = (i: InspectionListItem) => {
    const fullName = i.createdBy ? `${i.createdBy.firstName} ${i.createdBy.lastName}`.trim() : '';
    return (
      <span className='inspection-card__footer'>
        {fullName ? (
          <span className='inspection-card__creator'>
            <Avatar
              initials={initialsOf(fullName)}
              size={28}
              tone={toneOf(i.createdBy?.id || fullName)}
            />
            <span className='inspection-card__creator-label'>
              {t('text_created_by')}
              <span className='inspection-card__creator-name'>{fullName}</span>
            </span>
          </span>
        ) : (
          <span />
        )}
        {onEdit && (
          <Button variant='secondary' size='sm' iconStart='square-pen' onClick={() => onEdit(i)}>
            {t('inspection_action_edit')}
          </Button>
        )}
      </span>
    );
  };

  return (
    <>
      <DataCards
        items={items}
        itemKey={(i) => i.id}
        minCardWidth={300}
        loading={loading}
        error={error}
        loadingText={t('text_loading')}
        errorText={t('error_generic')}
        empty={
          <>
            <Icon name={archivedTab ? 'archive' : 'clipboard-check'} size={26} />
            {t('inspection_empty')}
          </>
        }
        renderCard={renderCard}
        renderFooter={renderFooter}
      />
      <CardsLoadMore
        hasMore={hasMore}
        loading={loadingMore}
        onLoadMore={onLoadMore}
        label={t('text_load_more')}
      />
    </>
  );
};

export default InspectionCards;
